import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8888'

interface User {
  id: number
  email: string
  display_name: string
  username?: string
  role: string
  plan?: string
  avatar_url?: string
  preferred_language?: string
  bio?: string
  [k: string]: unknown
}

interface Auth {
  user: User | null
  token: string | null
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, displayName: string, role?: string, preferred_language?: string) => Promise<void>
  logout: () => void
  reloadUser: () => Promise<void>
  isAuthenticated: boolean
  isLoading: boolean
}

const Ctx = createContext<Auth | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'))
  const [isLoading, setIsLoading] = useState(() => !!localStorage.getItem('token'))

  const loadUser = useCallback(async (t: string) => {
    try {
      const r = await fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${t}` } })
      if (r.ok) {
        const u = await r.json() as User
        setUser(u)
        setToken(t)
      } else {
        localStorage.removeItem('token')
        setToken(null)
        setUser(null)
      }
    } catch {
      localStorage.removeItem('token')
      setToken(null)
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    const t = localStorage.getItem('token')
    if (t) { void loadUser(t) } else { setIsLoading(false) }
  }, [loadUser])

  const login = async (email: string, password: string) => {
    const r = await fetch(`${API}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
    const d = await r.json() as { token?: string; access_token?: string; user?: User; error?: string; detail?: string }
    if (d.error || d.detail) throw new Error(d.error || d.detail)
    const tk = d.token || d.access_token || ''
    if (!tk) throw new Error('No token received')
    localStorage.setItem('token', tk)
    setToken(tk)
    if (d.user) setUser(d.user)
  }

  const register = async (email: string, password: string, displayName: string, role = 'user', preferred_language = 'en') => {
    const r = await fetch(`${API}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, display_name: displayName, role, preferred_language }),
    })
    const d = await r.json() as { token?: string; access_token?: string; user?: User; error?: string; detail?: string }
    if (d.error || d.detail) throw new Error(d.error || d.detail)
    const tk = d.token || d.access_token || ''
    if (tk) { localStorage.setItem('token', tk); setToken(tk) }
    if (d.user) setUser(d.user)
  }

  const logout = () => {
    localStorage.removeItem('token')
    setToken(null)
    setUser(null)
    window.location.href = '/login'
  }

  const reloadUser = async () => {
    const t = localStorage.getItem('token')
    if (t) await loadUser(t)
  }

  return (
    <Ctx.Provider value={{ user, token, login, register, logout, reloadUser, isAuthenticated: !!user, isLoading }}>
      {children}
    </Ctx.Provider>
  )
}

export function useAuth() {
  const c = useContext(Ctx)
  if (!c) throw new Error('useAuth must be used within AuthProvider')
  return c
}
